import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import contractPartyService from '../../services/contractPartyService.js';
import { Users, Plus } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkMobileClass } from '../common/uiClasses.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';


const ContractPartyList = () => {
  const { t } = useTranslation();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  const fetchParties = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;

      const response = await contractPartyService.getAll(currentPage, perPage, filters);
      setParties((prev) => mergeLoadedPage(currentPage, response.data, prev));
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load contract parties');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchParties,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, statusFilter],
    debounceWhen: search,
    debounceMs: 0,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = async (partyId, partyName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${partyName}"? This action cannot be undone.`,
      'Delete Contract Party'
    );

    if (confirmed) {
      try {
        await contractPartyService.delete(partyId);
        Alert.success('Contract party deleted successfully!');

        if (parties.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchParties();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete contract party');
      }
    }
  };

  const toggleDropdown = (partyId) => {
    setActiveDropdown(activeDropdown === partyId ? null : partyId);
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || statusFilter;

  const getStatusBadgeClass = (status) =>
    status === 'active'
      ? 'bg-green-100 text-green-700 border-green-200'
      : 'bg-red-100 text-red-700 border-red-200';

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Contract Parties"
        icon={Users}
        className="mb-4 sm:mb-6"
      >
        <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/contract-parties/add')}>
          Add Contract Party
        </Button>
      </ListPageHeader>

      <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
              <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder="Name, email, contact..."
              inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
            />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
      </ListFilterPanel>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading contract parties..." />
          </div>
        ) : parties.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">👥</div>
            <p className="text-gray-600 text-lg font-semibold">No contract parties found</p>
            <p className="text-gray-500 mt-2">Try adjusting your filters or add a new contract party</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden xl:block w-full overflow-x-auto">
              <table className="w-full min-w-[1000px] border-collapse">
                <thead className="bg-gray-50 rounded-t-xl">
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap min-w-[140px]">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap min-w-[140px]">{t('common.company')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">GST No.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('common.createdBy')}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {parties.map((party, index) => (
                    <tr
                      key={party.id}
                      className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-5 py-3 text-sm whitespace-nowrap">
                        <span
                          onClick={() => navigate(`/contract-parties/view/${party.id}`)}
                          className="font-regular text-secondary hover:text-secondary hover:underline cursor-pointer"
                        >
                          {party.name}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-900 break-words">{party.email}</td>
                      <td className="px-5 py-3 text-sm text-gray-900 whitespace-nowrap">{party.contact}</td>
                      <td className="px-5 py-3 text-sm text-gray-900 break-words">{party.company_name}</td>
                      <td className="px-5 py-3 text-sm text-gray-900 whitespace-nowrap">{party.gst_number || '-'}</td>
                      <td className="px-5 py-3 text-sm whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusBadgeClass(party.status)}`}>
                          {capitalizeStatus(party.status)}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-900">
                        <span className="truncate max-w-[120px] inline-block" title={party.created_by}>
                          {party.created_by}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm text-center">
                        <RowActionsMenu
                          isOpen={activeDropdown === party.id}
                          onToggle={() => toggleDropdown(party.id)}
                          onClose={() => setActiveDropdown(null)}
                          menuRef={dropdownRef}
                          onView={() => navigate(`/contract-parties/view/${party.id}`)}
                          onEdit={() => navigate(`/contract-parties/edit/${party.id}`)}
                          onDelete={() => handleDelete(party.id, party.name)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Tablet & Mobile cards */}
            <div className="xl:hidden space-y-3 p-4">
              {parties.map((party) => (
                <div key={`card-${party.id}`} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 border-l-4 border-l-blue-500">
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <div className="min-w-0">
                      <span
                        onClick={() => navigate(`/contract-parties/view/${party.id}`)}
                        className={listIdLinkMobileClass}
                      >
                        {party.name}
                      </span>
                      <p className="text-xs text-gray-400 mt-0.5 break-all">{party.email}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusBadgeClass(party.status)}`}>
                        {capitalizeStatus(party.status)}
                      </span>
                      <RowActionsMenu
                        isOpen={activeDropdown === party.id}
                        onToggle={() => toggleDropdown(party.id)}
                        onClose={() => setActiveDropdown(null)}
                        menuRef={dropdownRef}
                        onView={() => navigate(`/contract-parties/view/${party.id}`)}
                        onEdit={() => navigate(`/contract-parties/edit/${party.id}`)}
                        onDelete={() => handleDelete(party.id, party.name)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 font-medium">Contact</p>
                      <p className="font-semibold text-gray-800">{party.contact}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 font-medium">{t('common.company')}</p>
                      <p className="font-semibold text-gray-800 break-words">{party.company_name}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 font-medium">GST No.</p>
                      <p className="font-semibold text-gray-800">{party.gst_number || '-'}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 font-medium">{t('common.createdBy')}</p>
                      <p className="font-semibold text-gray-800 break-words">{party.created_by}</p>
                    </div>
                  </div>
                </div>
              ))}

            </div>
            <ListPagination
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading && currentPage > 1}
              onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
            />
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ContractPartyList;
